/**
 * FoamAccumulator — persistent whitecap trails for a Gerstner water body.
 *
 * The in-shader Jacobian mask is instantaneous: foam pops in exactly at the
 * folding crest and vanishes the moment the wave relaxes. Real whitecaps
 * LINGER — bubbles decay over seconds. This accumulator keeps a small CPU
 * grid over the water plane where each cell holds
 *
 *   foam ← max(instant(J), foam · e^(−dt/persistence))
 *
 * uploaded as a single-channel DataTexture the material samples in local UV.
 * CPU on purpose: identical behavior on WebGPU and WebGL2, unit-testable in
 * Node, and cheap at the default 128² / 20 Hz cadence (the high-frequency
 * crisp mask stays per-pixel in the shader; this texture only carries the
 * low-frequency lingering trails).
 */

import * as THREE from 'three/webgpu';
import { IDENTITY_MODULATION, sampleJacobian, type GerstnerWave, type WaveModulation } from './GerstnerWaves.js';
import { isWaveProvider, type WaveProvider } from './WaveProvider.js';

export interface FoamAccumulatorConfig {
  /** Water plane size (world units). */
  sizeX: number;
  sizeZ: number;
  /** Grid resolution per side. Default 128 (clamped 16..256). */
  resolution?: number;
  /** Foam half-life feel: e-folding decay time in seconds. Default 2.5. */
  persistence?: number;
  /** Jacobian value where foam saturates (J at/below this → full foam). Default 0.35. */
  jacobianFull?: number;
  /** Jacobian value where foam starts (J above this → none). Default 0.75. */
  jacobianStart?: number;
  /** Update cadence in Hz (decay integrates elapsed time exactly). Default 20. */
  updateHz?: number;
}

export class FoamAccumulator {
  readonly texture: THREE.DataTexture;
  private readonly data: Uint8Array;
  private readonly res: number;
  private readonly cfg: Required<FoamAccumulatorConfig>;
  private accum = 0;
  /** Runtime sea state (wind): the SAME modulation the material's uniforms carry. */
  private mod: Readonly<WaveModulation> = IDENTITY_MODULATION;

  /**
   * `waves` may be a WaveProvider (shader gaps R5): its `sampleJacobian` is then the whitecap source, evaluated at the
   * grid's ABSOLUTE cells (the provider maps them into its own frame — the Gerstner provider into render space, where the
   * material draws its crests; the FFT provider onto its patch). A plain wave list keeps the old call exactly.
   */
  constructor(private waves: GerstnerWave[] | WaveProvider, config: FoamAccumulatorConfig) {
    this.cfg = {
      sizeX: config.sizeX,
      sizeZ: config.sizeZ,
      resolution: Math.min(256, Math.max(16, Math.floor(config.resolution ?? 128))),
      persistence: Math.max(0.05, config.persistence ?? 2.5),
      jacobianFull: config.jacobianFull ?? 0.35,
      jacobianStart: config.jacobianStart ?? 0.75,
      updateHz: Math.min(60, Math.max(1, config.updateHz ?? 20)),
    };
    this.res = this.cfg.resolution;
    this.data = new Uint8Array(this.res * this.res);
    this.texture = new THREE.DataTexture(this.data, this.res, this.res, THREE.RedFormat);
    this.texture.wrapS = THREE.ClampToEdgeWrapping;
    this.texture.wrapT = THREE.ClampToEdgeWrapping;
    this.texture.magFilter = THREE.LinearFilter;
    this.texture.minFilter = THREE.LinearFilter;
    this.texture.colorSpace = THREE.NoColorSpace;
    this.texture.needsUpdate = true;
  }

  /** Swap the wave set (sea-state change) without losing existing foam. */
  setWaves(waves: GerstnerWave[] | WaveProvider): void {
    this.waves = waves;
  }

  /** Follow the water material's sea-state uniforms (amplitude scale + heading). */
  setModulation(mod: Readonly<WaveModulation>): void {
    this.mod = mod;
  }

  /**
   * Advance the accumulator. `time` is the shared water clock; `dt` the frame
   * delta; (offsetX, offsetZ) the mesh world position so grid cells sample
   * the same world-anchored wave field the shader displaces.
   * Returns true when the texture was refreshed this call (throttled).
   */
  update(time: number, dt: number, offsetX: number, offsetZ: number): boolean {
    this.accum += dt;
    const interval = 1 / this.cfg.updateHz;
    if (this.accum < interval) return false;
    const elapsed = this.accum;
    this.accum = 0;

    const decay = Math.exp(-elapsed / this.cfg.persistence);
    const { sizeX, sizeZ, jacobianFull, jacobianStart } = this.cfg;
    const n = this.res;
    const invSpan = 1 / Math.max(1e-6, jacobianStart - jacobianFull);

    for (let row = 0; row < n; row++) {
      const z = (row / (n - 1) - 0.5) * sizeZ + offsetZ;
      for (let col = 0; col < n; col++) {
        const x = (col / (n - 1) - 0.5) * sizeX + offsetX;
        const j = isWaveProvider(this.waves) ? this.waves.sampleJacobian(x, z, time, this.mod) : sampleJacobian(this.waves, x, z, time, this.mod);
        // 1 at/below jacobianFull, 0 at/above jacobianStart (linear ramp).
        const instant = Math.min(1, Math.max(0, (jacobianStart - j) * invSpan));
        const i = row * n + col;
        const decayed = this.data[i] * decay;
        const next = Math.max(instant * 255, decayed);
        this.data[i] = next > 1 ? next : 0; // snap the tail to zero
      }
    }
    this.texture.needsUpdate = true;
    return true;
  }

  /** Foam level 0..1 at local plane coordinates (for tests/queries). */
  foamAtLocal(x: number, z: number): number {
    const n = this.res;
    const col = Math.min(n - 1, Math.max(0, Math.round((x / this.cfg.sizeX + 0.5) * (n - 1))));
    const row = Math.min(n - 1, Math.max(0, Math.round((z / this.cfg.sizeZ + 0.5) * (n - 1))));
    return this.data[row * n + col] / 255;
  }

  dispose(): void {
    this.texture.dispose();
  }
}
